import DataObjectIcon from "@mui/icons-material/DataObject";
import Popper from "@mui/material/Popper";
import { VirtualElement } from "@popperjs/core";
import classNames from "classnames";
import { RefObject } from "react";
import { Variable } from "../../models/interactions/Variable";
import {
  getBuiltInFriendlyLabel,
  isBuiltInVariable,
} from "../../models/interactions/BuiltInSequenceVariables";

function getVariableSecondaryLabel(variable: Variable): string {
  if (isBuiltInVariable(variable)) {
    return variable.name;
  }

  if (typeof variable.value === "boolean") {
    return variable.value ? "True" : "False";
  }

  if (variable.value === null || variable.value === undefined) {
    return "";
  }

  return String(variable.value);
}

interface VariableSuggestionPopperProps {
  open: boolean;
  anchorEl: HTMLElement | VirtualElement | null;
  width?: number;
  maxWidth?: number;
  suggestions: Variable[];
  selectedIndex: number;
  menuRef: RefObject<HTMLDivElement>;
  onSelect: (variable: Variable) => void;
  disablePortal?: boolean;
  offset?: [number, number];
  menuClassName?: string;
  itemClassName?: string;
  selectedItemClassName?: string;
  iconClassName?: string;
  labelClassName?: string;
  secondaryLabelClassName?: string;
}

export default function VariableSuggestionPopper({
  open,
  anchorEl,
  width,
  maxWidth,
  suggestions,
  selectedIndex,
  menuRef,
  onSelect,
  disablePortal = false,
  offset = [0, 4],
  menuClassName,
  itemClassName,
  selectedItemClassName,
  iconClassName,
  labelClassName,
  secondaryLabelClassName,
}: VariableSuggestionPopperProps) {
  if (!open || suggestions.length === 0) return null;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      disablePortal={disablePortal}
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: {
            offset,
          },
        },
      ]}
      style={{
        zIndex: 1400,
        width,
        maxWidth,
      }}
    >
      <div
        ref={menuRef}
        data-testid="variable-suggestion-menu"
        className={classNames(
          "z-40 flex max-h-[200px] flex-col overflow-y-auto rounded-[10px] border-[1px] border-creator-border bg-creator-surface p-[6px] shadow-lg pit-creator-scrollbar",
          menuClassName
        )}
        onWheel={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerMove={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {suggestions.map((variable, index) => {
          const isSelected = index === selectedIndex;
          const secondaryLabel = getVariableSecondaryLabel(variable);

          return (
            <button
              type="button"
              key={variable.id}
              data-testid={`variable-suggestion-${variable.id}`}
              data-suggestion-index={index}
              aria-selected={isSelected}
              className={classNames(
                "flex items-center gap-[10px] rounded-[5px] px-[10px] py-[8px] text-left hover:bg-creator-primary-background",
                itemClassName,
                isSelected && (selectedItemClassName ?? "bg-creator-primary-background")
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onSelect(variable);
              }}
            >
              <DataObjectIcon
                sx={{ fontSize: 18 }}
                className={classNames("text-creator-primary", iconClassName)}
              />
              <div className="flex min-w-0 flex-col">
                <span
                  className={classNames(
                    "truncate text-[14px] font-medium text-creator-text-sub",
                    labelClassName
                  )}
                >
                  {isBuiltInVariable(variable)
                    ? getBuiltInFriendlyLabel(variable.name)
                    : variable.name}
                </span>
                {secondaryLabel && (
                  <span
                    className={classNames(
                      "truncate text-[11px] text-creator-text-soft",
                      secondaryLabelClassName
                    )}
                  >
                    {secondaryLabel}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </Popper>
  );
}
